import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext.jsx';
import * as authService from '../../services/auth.service.js';
import GeIcon from '../common/GeIcon.jsx';
import { maskAccountPhone } from '../../utils/maskAccountPhone.js';
import './PhoneReverifyBanner.css';

/**
 * F-26 — bandeau « Confirmez votre numéro ».
 * Un compte créé avec un code reçu par EMAIL (repli quand l'appel échoue) n'a pas
 * prouvé qu'il contrôle son SIM : le serveur refuse ses opérations d'argent
 * (403 PHONE_VERIFICATION_REQUIRED) tant qu'un code SMS/appel n'a pas été validé.
 * Ce bandeau, affiché sur toutes les pages tant que `phone_verification_pending`
 * est vrai (non masquable, décision fondateur 26/09), mène à ce code.
 */

function apiError(err, fallback) {
  const msg = err?.response?.data?.error;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
}

export default function PhoneReverifyBanner() {
  const { user, updateUser } = useAuth();
  const [sentOnce, setSentOnce] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [channel, setChannel] = useState('sms');
  const [mockCode, setMockCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Après succès : « ✓ Numéro confirmé » reste lisible quelques secondes, puis disparaît.
  useEffect(() => {
    if (!confirmed) return undefined;
    const t = setTimeout(() => setConfirmed(false), 5000);
    return () => clearTimeout(t);
  }, [confirmed]);

  if (!user) return null;
  if (!user.phone_verification_pending && !confirmed) return null;

  if (confirmed) {
    return (
      <div className="phone-reverify-banner phone-reverify-banner--done" role="status">
        <div className="phone-reverify-inner">
          <p className="phone-reverify-text">✓ Numéro confirmé — les opérations d’argent sont débloquées.</p>
        </div>
      </div>
    );
  }

  const handleSend = async (requested = 'sms') => {
    setSending(true);
    setError('');
    setMessage('');
    setMockCode('');
    try {
      // F-28 — toujours le canal demandé : sans lui, le serveur prend le canal principal
      // (l'appel pour US/CA, seuls comptes passés par le repli email) et « SMS » rappelait.
      const res = await authService.sendReverifyCode({ channel: requested });
      if (!res?.success) {
        setError(res?.error || 'Impossible d’envoyer le code.');
        return;
      }
      const used = res.data?.channel || requested;
      setSentOnce(true);
      setCode('');
      setChannel(used);
      setCooldown(60);
      setMessage(used === 'voice'
        ? 'Appel en cours : répondez pour entendre votre code.'
        : 'Code envoyé par SMS au numéro de votre compte.');
      if (res.data?.mock_code) setMockCode(res.data.mock_code);
    } catch (err) {
      setError(apiError(err, 'Impossible d’envoyer le code.'));
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) {
      setError('Saisissez le code reçu.');
      return;
    }
    setVerifying(true);
    setError('');
    setMessage('');
    try {
      const res = await authService.verifyReverifyCode(code.trim());
      if (!res?.success) {
        setError(res?.error || 'Code invalide.');
        return;
      }
      let fresh = null;
      try {
        const me = await authService.getMe();
        if (me?.success && me.data) fresh = me.data;
      } catch {
        // /auth/me indisponible : on applique localement ce que le serveur vient de confirmer.
      }
      setConfirmed(true);
      updateUser(fresh || { ...user, phone_verification_pending: false });
    } catch (err) {
      setError(apiError(err, 'Code invalide.'));
    } finally {
      setVerifying(false);
    }
  };

  const masked = maskAccountPhone(user.telephone);
  const otherChannel = channel === 'voice' ? 'sms' : 'voice';

  return (
    <section className="phone-reverify-banner" aria-labelledby="phone-reverify-title">
      <div className="phone-reverify-inner">
        <GeIcon name="validation" className="phone-reverify-icon" size="1.25em" />
        <div className="phone-reverify-body">
          <p className="phone-reverify-text">
            <strong id="phone-reverify-title">Confirmez votre numéro{masked ? ` ${masked}` : ''}</strong>
            {' '}par SMS ou appel : un code reçu par email ne suffit pas pour les opérations d’argent.
          </p>

          {sentOnce && (
            <form className="phone-reverify-form" onSubmit={handleVerify} noValidate>
              <label htmlFor="phone-reverify-code" className="phone-reverify-label">Code reçu</label>
              <div className="phone-reverify-row">
                <input
                  id="phone-reverify-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="6 chiffres"
                  disabled={verifying}
                  className="phone-reverify-input"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'phone-reverify-error' : undefined}
                />
                <button type="submit" className="btn btn-primary phone-reverify-btn" disabled={verifying || code.length < 4}>
                  {verifying ? 'Vérification…' : 'Confirmer'}
                </button>
              </div>
              <div className="phone-reverify-links">
                <button
                  type="button"
                  className="phone-reverify-link"
                  onClick={() => handleSend(channel)}
                  disabled={sending || cooldown > 0}
                >
                  {cooldown > 0 ? `Renvoyer le code (${cooldown}s)` : 'Renvoyer le code'}
                </button>
                {/* F-28 — l'autre canal toujours proposé : fallbackChannel vaut « email »
                    pour US/CA, repli qui n'existe pas dans ce flux. */}
                <button
                  type="button"
                  className="phone-reverify-link"
                  onClick={() => handleSend(otherChannel)}
                  disabled={sending || cooldown > 0}
                >
                  {otherChannel === 'voice' ? 'Pas reçu ? Recevoir un appel' : 'Pas reçu ? Recevoir un SMS'}
                </button>
              </div>
            </form>
          )}

          {mockCode && (
            <p className="phone-reverify-mock" role="status">
              <span>Mode test — code :</span> <strong>{mockCode}</strong>
            </p>
          )}
          {message && !error && <p className="phone-reverify-message" role="status" aria-live="polite">{message}</p>}
          {error && <p className="field-error" id="phone-reverify-error" role="alert">{error}</p>}
        </div>

        {!sentOnce && (
          <button
            type="button"
            className="btn btn-secondary phone-reverify-btn phone-reverify-send"
            onClick={() => handleSend('sms')}
            disabled={sending}
          >
            {sending ? 'Envoi…' : 'Recevoir un code par SMS'}
          </button>
        )}
      </div>
    </section>
  );
}
